import { CSSProperties } from "react";
import { DashboardColors } from "@src/types/dashboards";

// Lift React's type for CSS custom properties (variables starting with
// `--`). Without this, `style={{ "--color-cp-widgets-bg": "#ff0000" }}`
// fails TS in strict mode. Module augmentation keeps the runtime cost
// at zero — no extra runtime check, just broader compile-time editing.
declare module "react" {
  interface CSSProperties {
    [key: `--${string}`]: string | number | undefined;
  }
}

// snake_case palette key -> CSS variable name. To add a new palette
// group: add a TS key on `DashboardColors` AND an entry here. The two
// stay in lockstep; the picker-roadmap rule says future themables land
// inside `presentation`, and this map is the bridge that turns BE keys
// into style overrides.
const KEY_TO_CSS_VAR: Record<string, string> = {
  canvas_bg: "--color-cp-canvas-bg",
  subheader_bg: "--color-cp-subheader-bg",
  text_primary: "--color-cp-text-primary",
  text_secondary: "--color-cp-text-secondary",
  widgets_bg: "--color-cp-widgets-bg",
  border: "--color-cp-border",
  border_hover: "--color-cp-border-hover",
  // Widget-text theming. Three new keys land below the dashboard
  // chrome slots: title comes first because it's the most prominent
  // text rendered inside each tile. Primary/secondary follow so the
  // user can read the value/timestamp distinction in one glance
  // without scrolling across the existing 7 swatches.
  widget_title: "--color-cp-widget-title",
  widget_primary: "--color-cp-widget-primary",
  widget_secondary: "--color-cp-widget-secondary",
  // Future keys (e.g. "text_disabled") extend this map.
};

// Maps a `DashboardColors` blob into the inline `style` we drop on the
// central-panel root. Returns `undefined` when no key on the palette is
// set, so React leaves the `style` prop off the DOM element entirely
// and the SCSS fallbacks render unchanged — that's what preserves the
// byte-identical visual of unthemed dashboards.
//
// Convention: a key absent in BE or held as `null` means "leave this
// themable element at the SCSS default," so we skip those keys here
// rather than write `--color-cp-foo: null` (which would be invalid CSS).
export const paletteColorsToStyle = (
  colors: DashboardColors | null | undefined,
): CSSProperties | undefined => {
  if (!colors) return undefined;
  let hasAny = false;
  const style: Record<string, string> = {};
  for (const [key, value] of Object.entries(colors)) {
    if (typeof value !== "string") continue;
    const cssVar = KEY_TO_CSS_VAR[key];
    if (!cssVar) continue;
    style[cssVar] = value;
    hasAny = true;
  }
  return hasAny ? (style as CSSProperties) : undefined;
};

// SCSS fallback values for every themable key. Mirrors the
// `var(--color-cp-X, #fallback)` declarations in `dashboards-page`,
// `dashboard-canvas-header`, `sub-header`, and `dashboard-tile-grid`.
// Drives the swatch's "default" color (no user theme applied yet) so
// the user sees the actual paint they would get after Reset, not
// blank tokens.
//
// `canvas_bg` is the trickiest one. The SCSS fallback in
// `dashboards-page__central-panel` is `transparent`, and the parent
// `round-border-component-container__gradient-border` has its own
// `background-color: #131b20` sitting one level above. So the
// user-visible paint of the unthemed canvas is `#131b20` — the swatch
// mirrors that hex (matches what the eye sees), even though the
// emitted CSS variable resolves to `transparent`. The two are
// visually identical (the gradient-border bg shows through the
// central-panel's transparency), but showing the gradient-border
// fill on the swatch is what users actually see, which is the
// behavior the picker is meant to teach.
//
// Hard-coded (vs reading `getComputedStyle` at render) keeps a
// single point of truth and dodges the DOM-reflection dependency;
// the SCSS values are documented here so any drift becomes a
// review-time spot-check.
export const DEFAULT_PALETTE: Record<keyof DashboardColors, string> = {
  canvas_bg: "#131b20", // round-border-component-container__gradient-border bg
  subheader_bg: "#142d33", // colors.$backgroundLight
  text_primary: "#e6f1f2", // colors.$white
  text_secondary: "#697477", // colors.$inactiveGrey
  widgets_bg: "#142d33", // colors.$backgroundLight
  border: "rgb(230 241 242 / 5%)", // colors.$white5
  border_hover: "rgb(2 118 128 / 40%)", // colors.$blue40
  widget_title: "#e6f1f2", // colors.$white — matches the existing __title rules
  widget_primary: "#e6f1f2", // colors.$white — matches the existing body text
  widget_secondary: "#697477", // colors.$inactiveGrey — matches the dashboard secondary / timestamp stamp
};
